import React from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import apiClient from '../utils/apiClient';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  cityInput: {
    '& .MuiInputBase-root': {
      borderRadius: '8px',
    },
  },
}));

export type PlaceType = {
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings: {
      offset: number;
      length: number;
    }[];
  };
  place_id: string;
  terms: any;
  types: string[];
  reference: string;
};

type LocationSearchInputProps = {
  onSelectPlace: (place: PlaceType) => void;
};

const transformPredictionToPlaceType = (prediction: any): PlaceType => {
  const parts = prediction.description.split(', ');
  const main_text = parts[0] || '';
  const secondary_text = parts.slice(1).join(', ') || '';

  return {
    description: prediction.description,
    reference: prediction.reference,
    structured_formatting: {
      main_text,
      secondary_text,
      main_text_matched_substrings: [{
        offset: 0,
        length: main_text.length
      }]
    },
    place_id: prediction.place_id,
    terms: prediction.terms || [],
    types: prediction.types || []
  };
};

export default function LocationSearchInput({ onSelectPlace }: LocationSearchInputProps) {
  const classes = useStyles();
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<PlaceType[]>([]);
  const [loading, setLoading] = React.useState(false);

  const searchAddresses = React.useCallback(
    async (query: string) => {
      if (!query.trim() || query.length < 2) {
        setOptions(value ? [value] : []);
        return;
      }

      setLoading(true);
      try {
        const response = await apiClient.get('/geocode/autocomplete', { input: query, useAnyAutocompleteType: true });
        const predictions = response?.predictions || [];

        const transformedPredictions = predictions.map(transformPredictionToPlaceType);

        let newOptions = [] as PlaceType[];
        if (value) {
          newOptions = [value];
        }
        if (transformedPredictions) {
          newOptions = [...newOptions, ...transformedPredictions];
        }

        setOptions(newOptions);
      } catch (error) {
        console.error('Error fetching address predictions:', error);
        setOptions(value ? [value] : []);
      } finally {
        setLoading(false);
      }
    },
    [value]
  );

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAddresses(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, searchAddresses]);

  return (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      loading={loading}
      value={value}
      onChange={(event: any, newValue: PlaceType | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onSelectPlace(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Add a location"
          variant="outlined"
          classes={{ root: classes.cityInput }}
          fullWidth
        />
      )}
      renderOption={(option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [match.offset, match.offset + match.length]),
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}
              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
      noOptionsText={loading ? "Loading..." : "No places found"}
    />
  );
}
